import React, {useState} from "react";
import {OrganisationDTO} from "../../types/OrganisationDTO";
import {Button, Confirm, Modal, Spinner, Tooltip} from "@tag/tag-components-react-v4";
import DataExportModal from "./DataExportModal/DataExportModal";
import styles from './CustomerMenuModal.module.scss'
import RemoveCustomerModal from "./RemoveCustomerModal/RemoveCustomerModal";
import AppRolesService from "../../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import OrganisationService from "../../services/OrganisationService";
import PushPoliciesModal from "./PushPoliciesModal/PushPoliciesModal";
import WorkspaceService from "../../services/WorkspaceService";
import { ProjectAccents } from "../../../General/config/projectAccents";

function CustomerMenuModal(props: {selectedOrganisation: OrganisationDTO, handleClose: Function, setAlert: Function, reloadCustomerData: Function}) {

    const [dataExportVisible, setDataExportVisible] = useState(false);
    const [terminateVisible, setTerminateVisible] = useState(false);
    const [pushPoliciesVisible, setPushPoliciesVisible] = useState(false);
    const [syncUserLocationsConfirmationModalVisible, setSyncUserLocationsConfirmationModalVisible] = useState(false);
    const [loading, setLoading] = useState<string|boolean>(false);


    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const canDeleteEnv = AppRolesService.canUserDeleteEnvironments(accounts, isAuthenticated);
    const canSoftDelEnv = AppRolesService.canUserSoftDeleteAcppEnvironments(accounts, isAuthenticated);
    const canUserConfigureACPP = AppRolesService.canUserConfigureACPP(accounts, isAuthenticated);

    const delay = (ms:number) => new Promise(res => setTimeout(res, ms));

    const handleSoftDelete = async (reason: string) => {
        let errorMessage = '';
        setLoading('Starting...');

        try {
            const requestLockResponse = await OrganisationService.requestSoftDelete(props.selectedOrganisation.id, props.selectedOrganisation.environmentName, reason);
            
            const taskId = requestLockResponse.taskId;
            
            let status;
            do {
                status = await OrganisationService.SoftDeleteStatus(taskId);
                setLoading(status.progressMessage);
                await delay(50);
            } while (!status.complete && !status.errorMessage);

            if (status.errorMessage) {
                errorMessage = status.errorMessage;
                throw new Error();
            }

            props.setAlert({type: 'success', message: 'Success - Please view terminations page'});
        } catch (e) {
            props.setAlert({type: 'error', message: errorMessage || 'An error has occured, please contact HSC Care DevOps'});
        } finally {
            props.handleClose();
            props.reloadCustomerData();
            setLoading(false);
        }
    }

    const deleteOrganisation = async (reason: string) => {
        if (!canDeleteEnv) {
            throw new Error()
        }

        setLoading(true);

        OrganisationService.deleteOrganisation(props.selectedOrganisation.environmentName, props.selectedOrganisation.id, reason)
            .then(() => {
                props.setAlert({type: 'success', message: 'Organisation Deleted'});
            })
            .catch((reason) => {
                props.setAlert({type: 'error', message: 'An error has occured, please contact HSC Care DevOps'});
            })
            .then(() => {
                setLoading(false)
                props.handleClose();
                props.reloadCustomerData();
            });
    }
    
    const onConfirmSyncUserLocations = () => {
        setLoading(true)
        
        WorkspaceService.syncUserLocations(props.selectedOrganisation.id, props.selectedOrganisation.environmentName)
            .then(() => {
                props.setAlert({type: 'success', message: 'Sync User Locations Process successful'});
            })
            .catch((reason) => {
                props.setAlert({type: 'error', message: reason.message || 'An error has occured, please contact HSC Care DevOps'});
            })
            .finally(() => {
                setLoading(false)
                setSyncUserLocationsConfirmationModalVisible(false);
            });
    }
    
    const renderPushPolicyTypesButton = () => {
        const button = (
            <div className={styles.CustomerMenuModalButtonElement}>
                <Button text={'Push Policy Types'}
                        disabled={!canUserConfigureACPP}
                        onClick={() => setPushPoliciesVisible(true)}/>
            </div>
        );
        
        return (
            canUserConfigureACPP ? button : 
                <Tooltip 
                    background={'dark'} 
                    tooltipFor={button}
                >You require 'Configure ACPP' role for this, please contact HSC DevOps</Tooltip>
        );
    }

    const renderSyncUserLocationsButton = () => {
        const button = (
            <div className={styles.CustomerMenuModalButtonElement}>
                <Button text={'Sync User Locations'}
                        disabled={!AppRolesService.canUserConfigureACPP(accounts, isAuthenticated)}
                        onClick={() => setSyncUserLocationsConfirmationModalVisible(true)}/>
            </div>
        );

        return (
            canUserConfigureACPP ? button :
                <Tooltip
                    background={'dark'}
                    tooltipFor={button}
                >You require 'Configure ACPP' role for this, please contact HSC DevOps</Tooltip>
        );
    }
    
    let content;
    if (loading) {
        content = <Spinner full={true}>{loading}</Spinner>;
    } else {
        content =
            <Modal
                visible={!!props.selectedOrganisation}
                accent={ProjectAccents.PoliciesAndProcedures}
                heading={props.selectedOrganisation?.displayedName}
                primaryButtonProps={{buttonStyle: {'display': 'none'}}}
                onCancelButtonClick={() => {
                    props.handleClose();
                }}
                onClose={() => props.handleClose()}
            >
                <div className={styles.CustomerMenuModalButtonContainer}>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Data Export'} onClick={() => setDataExportVisible(true)}/>
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Termination'} onClick={() => setTerminateVisible(true)}/>
                    </div>
                    {renderPushPolicyTypesButton()}
                    {renderSyncUserLocationsButton()}
                </div>
            </Modal>

    }

    return (
        <>
            {content}
            {dataExportVisible ?
                <DataExportModal
                    visible={dataExportVisible}
                    selectedOrganisation={props.selectedOrganisation}
                    handleClose={() => setDataExportVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    setAlert={props.setAlert}
                /> : undefined
            }
            {terminateVisible ?
                <RemoveCustomerModal
                    visible={terminateVisible}
                    selectedOrganisation={props.selectedOrganisation}
                    handleClose={() => setTerminateVisible(false)}
                    handleDelete={deleteOrganisation}
                    handleSoftDelete={handleSoftDelete}
                    canDeleteEnv={canDeleteEnv}
                    canSoftDeleteEnv={canSoftDelEnv}
                /> : undefined
            }
            {pushPoliciesVisible ?
                <PushPoliciesModal
                    visible={pushPoliciesVisible}
                    selectedOrganisation={props.selectedOrganisation}
                    handleClose={() => setPushPoliciesVisible(false)}
                    isUserAllowed={AppRolesService.canUserConfigureACPP(accounts, isAuthenticated)}
                    setAlert={props.setAlert}
                /> : undefined
            }
            
            {/*Confirmation modals*/}
            <Confirm
                visible={syncUserLocationsConfirmationModalVisible}
                type="question"
                heading="Confirmation"
                accent={ProjectAccents.PoliciesAndProcedures}
                onPrimaryButtonClick={() => onConfirmSyncUserLocations()}
                onCancelButtonClick={() => setSyncUserLocationsConfirmationModalVisible(false)}
                onClose={() => setSyncUserLocationsConfirmationModalVisible(false)}
            >
                Are you sure you want to Sync User Locations?
            </Confirm>
            
        </>
    )

}

export default CustomerMenuModal;
